import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Package,
  Search,
  FileText,
  Users,
  ShoppingCart,
  Bell,
  Inbox,
  FolderOpen,
  Image,
  Calendar,
  MessageSquare,
  Plus,
  LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type EmptyStateType =
  | 'products'
  | 'search'
  | 'documents'
  | 'users'
  | 'cart'
  | 'notifications'
  | 'inbox'
  | 'files'
  | 'images'
  | 'events'
  | 'comments'
  | 'generic';

interface EmptyStateProps {
  type?: EmptyStateType;
  title?: string;
  description?: string;
  icon?: LucideIcon;
  action?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
  secondaryAction?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
  children?: ReactNode;
  className?: string;
}

const typeConfig: Record<EmptyStateType, { icon: LucideIcon; title: string; description: string }> = {
  products: {
    icon: Package,
    title: 'Nenhum produto encontrado',
    description: 'Não encontrámos produtos que correspondam aos seus critérios.',
  },
  search: {
    icon: Search,
    title: 'Sem resultados',
    description: 'Não encontrámos nada para a sua pesquisa. Tente outros termos.',
  },
  documents: {
    icon: FileText,
    title: 'Sem documentos',
    description: 'Ainda não existem documentos nesta secção.',
  },
  users: {
    icon: Users,
    title: 'Nenhum utilizador',
    description: 'Ainda não existem utilizadores registados.',
  },
  cart: {
    icon: ShoppingCart,
    title: 'Carrinho vazio',
    description: 'O seu carrinho está vazio. Adicione produtos para continuar.',
  },
  notifications: {
    icon: Bell,
    title: 'Sem notificações',
    description: 'Não tem notificações de momento.',
  },
  inbox: {
    icon: Inbox,
    title: 'Caixa de entrada vazia',
    description: 'Não tem mensagens novas.',
  },
  files: {
    icon: FolderOpen,
    title: 'Nenhum ficheiro',
    description: 'Ainda não existem ficheiros nesta pasta.',
  },
  images: {
    icon: Image,
    title: 'Sem imagens',
    description: 'Ainda não existem imagens nesta galeria.',
  },
  events: {
    icon: Calendar,
    title: 'Sem eventos',
    description: 'Não existem eventos agendados.',
  },
  comments: {
    icon: MessageSquare,
    title: 'Sem comentários',
    description: 'Seja o primeiro a comentar.',
  },
  generic: {
    icon: FolderOpen,
    title: 'Sem conteúdo',
    description: 'Não existe conteúdo para mostrar.',
  },
};

const EmptyState = ({
  type = 'generic',
  title,
  description,
  icon: CustomIcon,
  action,
  secondaryAction,
  children,
  className,
}: EmptyStateProps) => {
  const config = typeConfig[type];
  const Icon = CustomIcon || config.icon;
  const displayTitle = title || config.title;
  const displayDescription = description || config.description;

  const ActionButton = ({ actionConfig }: { actionConfig: NonNullable<EmptyStateProps['action']> }) => {
    if (actionConfig.href) {
      return (
        <Button asChild>
          <Link to={actionConfig.href}>
            <Plus className="h-4 w-4 mr-2" />
            {actionConfig.label}
          </Link>
        </Button>
      );
    }
    return (
      <Button onClick={actionConfig.onClick}>
        <Plus className="h-4 w-4 mr-2" />
        {actionConfig.label}
      </Button>
    );
  };

  const SecondaryButton = ({ actionConfig }: { actionConfig: NonNullable<EmptyStateProps['secondaryAction']> }) => {
    if (actionConfig.href) {
      return (
        <Button variant="outline" asChild>
          <Link to={actionConfig.href}>{actionConfig.label}</Link>
        </Button>
      );
    }
    return (
      <Button variant="outline" onClick={actionConfig.onClick}>
        {actionConfig.label}
      </Button>
    );
  };

  return (
    <div className={cn("flex flex-col items-center justify-center py-12 px-4 text-center", className)}>
      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-6">
        <Icon className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="text-xl font-semibold mb-2">{displayTitle}</h3>
      <p className="text-muted-foreground max-w-md mb-6">{displayDescription}</p>

      {(action || secondaryAction) && (
        <div className="flex flex-col sm:flex-row gap-3">
          {action && <ActionButton actionConfig={action} />}
          {secondaryAction && <SecondaryButton actionConfig={secondaryAction} />}
        </div>
      )}

      {children}
    </div>
  );
};

export default EmptyState;
